import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Brain, Clock, Check, X, ChevronRight, Trophy, RotateCcw, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { quizBank } from '../data/features';
import { useAuth, API } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import axios from 'axios';

function formatTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function QuizPage() {
  const { user, login } = useAuth();
  const [selectedId, setSelectedId] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [finished, setFinished] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);
  const navigate = useNavigate();

  const quiz = selectedId ? quizBank[selectedId] : null;

  useEffect(() => {
    if (!quiz || finished) return;
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) { clearInterval(timerRef.current); finish(); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [quiz, finished]);

  const startQuiz = (id) => {
    setSelectedId(id);
    setCurrent(0); setAnswers({}); setFinished(false); setSubmitted(false);
    setTimeLeft(quizBank[id].duration);
  };

  const pick = (idx) => setAnswers({ ...answers, [current]: idx });
  const next = () => current < quiz.questions.length - 1 ? setCurrent(current + 1) : finish();

  const score = () => quiz ? Object.entries(answers).filter(([i, a]) => quiz.questions[+i].answer === a).length : 0;

  const finish = () => {
    clearInterval(timerRef.current);
    setFinished(true);
  };

  const submitScore = async () => {
    if (!user) { login(); return; }
    if (submitted) return;
    const s = score();
    try {
      await axios.post(`${API}/quiz/submit`, {
        quiz_id: selectedId, score: s, total: quiz.questions.length,
        time_taken: quiz.duration - timeLeft, subject: quiz.titleEn,
      }, { withCredentials: true });
      setSubmitted(true);
      toast({ title: 'Score save ହୋଇଛି', description: `Leaderboard ରେ ଦେଖନ୍ତୁ` });
    } catch (e) {
      toast({ title: 'ତ୍ରୁଟି', description: 'Score save ହେଲା ନାହିଁ' });
    }
  };

  // -------- LIST view --------
  if (!quiz) {
    return (
      <div className="px-4 pt-4 space-y-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 font-medium">
          <ArrowLeft size={16} /> ମୁଖ୍ୟ ପେଜ କୁ ଫେରନ୍ତୁ
        </Link>
        <div className="rounded-3xl p-5 text-white shadow-lg" style={{ background: 'linear-gradient(135deg, #d946ef 0%, #ec4899 60%, #f43f5e 100%)' }}>
          <Brain size={26} />
          <h1 className="text-xl font-extrabold mt-2">Interactive Quiz</h1>
          <p className="text-xs text-white/90 mt-1">ସମୟ ସହିତ ପ୍ରଶ୍ନ · Instant Scoring · Leaderboard</p>
        </div>
        <div className="space-y-3">
          {Object.entries(quizBank).map(([id, q]) => (
            <button key={id} onClick={() => startQuiz(id)}
              className="w-full text-left bg-white border border-gray-200 rounded-2xl p-4 hover:border-purple-400 transition-all flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-purple-100 text-purple-700 flex items-center justify-center"><Brain size={20} /></div>
              <div className="flex-1">
                <div className="font-bold text-sm text-gray-900">{q.title}</div>
                <div className="text-[11px] text-gray-500">{q.titleEn} · {q.questions.length} ପ୍ରଶ୍ନ · {q.duration / 60} ମିନିଟ</div>
              </div>
              <ChevronRight size={20} className="text-gray-400" />
            </button>
          ))}
        </div>
        <Link to="/leaderboard" className="block bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
          <Trophy size={20} className="text-amber-600" />
          <div className="flex-1">
            <div className="font-bold text-sm text-amber-800">Leaderboard ଦେଖନ୍ତୁ</div>
            <div className="text-[11px] text-amber-700">Top scorers of Gurusevakendra</div>
          </div>
          <ChevronRight size={18} className="text-amber-600" />
        </Link>
      </div>
    );
  }

  // -------- FINISHED view --------
  if (finished) {
    const s = score();
    const pct = Math.round((s / quiz.questions.length) * 100);
    return (
      <div className="px-4 pt-4 space-y-4">
        <Card className="rounded-3xl p-6 border-emerald-300 bg-emerald-50 text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white flex items-center justify-center mx-auto shadow-lg"><Award size={40} /></div>
          <h2 className="font-extrabold text-2xl mt-3 text-gray-900">{pct}%</h2>
          <p className="text-sm text-gray-700 mt-1">{s} / {quiz.questions.length} ପ୍ରଶ୍ନର ସଠିକ ଉତ୍ତର</p>
          <p className="text-[11px] text-gray-500">{quiz.title} · ସମୟ: {formatTime(quiz.duration - timeLeft)}</p>
          <div className="flex gap-2 mt-4">
            <Button onClick={submitScore} disabled={submitted} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl">
              {submitted ? 'Saved ✓' : 'Score Save କରନ୍ତୁ'}
            </Button>
            <Button onClick={() => setSelectedId(null)} variant="outline" className="flex-1 rounded-xl gap-1">
              <RotateCcw size={14} /> ପୁଣି
            </Button>
          </div>
          {pct >= 60 && submitted && (
            <button onClick={() => navigate(`/certificate?name=${encodeURIComponent(user?.name || 'Student')}&quiz=${encodeURIComponent(quiz.titleEn)}&score=${pct}`)}
              className="mt-3 text-emerald-700 font-bold text-sm flex items-center gap-1 mx-auto">
              🏆 Certificate ପାଆନ୍ତୁ →
            </button>
          )}
        </Card>

        <div className="space-y-2">
          <h3 className="text-sm font-bold text-gray-800">Review Answers</h3>
          {quiz.questions.map((q, i) => {
            const ua = answers[i]; const correct = q.answer;
            return (
              <Card key={i} className="rounded-xl p-3 border-gray-200">
                <div className="flex items-start gap-2">
                  {ua === correct ? <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> : <X size={16} className="text-red-500 shrink-0 mt-0.5" />}
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-gray-900">{i + 1}. {q.q}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5">{q.qEn}</div>
                    <div className="mt-1 text-[11px] text-emerald-700">✓ ସଠିକ: {q.options[correct]}</div>
                    {ua !== undefined && ua !== correct && (
                      <div className="text-[11px] text-red-600">✗ ଆପଣଙ୍କର: {q.options[ua]}</div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // -------- ATTEMPT view --------
  const qq = quiz.questions[current];
  const selectedAns = answers[current];
  return (
    <div className="px-4 pt-4 space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => { if (window.confirm('Quiz ଛାଡ଼ି ଦେବେ?')) setSelectedId(null); }}
          className="inline-flex items-center gap-1 text-sm text-gray-600 font-medium">
          <ArrowLeft size={16} /> Exit
        </button>
        <div className={`text-sm font-bold px-3 py-1 rounded-full ${timeLeft < 60 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'} flex items-center gap-1`}>
          <Clock size={13} /> {formatTime(timeLeft)}
        </div>
      </div>

      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${((current + 1) / quiz.questions.length) * 100}%` }} />
      </div>

      <Card className="rounded-2xl p-5 border-gray-200">
        <div className="text-xs font-semibold text-purple-600">ପ୍ରଶ୍ନ {current + 1} / {quiz.questions.length}</div>
        <h2 className="text-lg font-extrabold text-gray-900 mt-1">{qq.q}</h2>
        <p className="text-xs text-gray-500 mt-1">{qq.qEn}</p>

        <div className="space-y-2 mt-4">
          {qq.options.map((opt, idx) => {
            const active = selectedAns === idx;
            return (
              <button key={idx} onClick={() => pick(idx)}
                className={`w-full text-left p-3 rounded-xl border-2 font-medium text-sm transition-all ${active ? 'border-purple-500 bg-purple-50 text-purple-900' : 'border-gray-200 bg-white text-gray-800 hover:border-purple-300'}`}>
                <span className={`inline-block w-6 h-6 rounded-full mr-2 text-center text-xs font-bold ${active ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{String.fromCharCode(65 + idx)}</span>
                {opt}
              </button>
            );
          })}
        </div>
      </Card>

      <div className="flex gap-2">
        {current > 0 && <Button variant="outline" onClick={() => setCurrent(current - 1)} className="flex-1 h-11 rounded-xl">← ପୂର୍ବ</Button>}
        <Button onClick={next} disabled={selectedAns === undefined} className="flex-1 h-11 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-bold">
          {current === quiz.questions.length - 1 ? 'ସମାପ୍ତ →' : 'ପରବର୍ତ୍ତୀ →'}
        </Button>
      </div>
    </div>
  );
}
